import React, { useState, useEffect, useMemo, useRef } from 'react'
import { 
  CalendarIcon, 
  ClockIcon, 
  UserIcon, 
  PlayCircleIcon, 
  DocumentTextIcon,
  SparklesIcon,
  BookOpenIcon,
  CheckBadgeIcon,
  ArrowRightIcon,
  VideoCameraIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  AcademicCapIcon,
  CheckIcon,
  DocumentArrowDownIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline'
import { 
  CheckCircleIcon as CheckCircleSolid,
  ClockIcon as ClockSolid,
  XCircleIcon as XCircleSolid,
  StarIcon
} from '@heroicons/react/24/solid'
import { Link } from 'react-router-dom'
import { StudentPageLayout } from '../../components/student/StudentPageLayout'
import { StudentSection } from '../../components/student/StudentSection'
import { DateHelper } from '../../utils/dateHelper'

interface Booking {
  id: string
  date: Date
  startTime: string
  endTime: string
  duration: number
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed'
  totalPrice: number
  notes?: string
  rejectionReason?: string
  paymentStatus?: 'pending' | 'paid' | 'failed'
  createdAt: Date
  updatedAt: Date
  course: {
    id: string
    title: string
    subject: string
    finalPricePerHour: number
    coverImage?: string
    description?: string
    learningObjectives?: string[]
    requirements?: {
      prerequisites?: string[]
    }
  }
  teacher: {
    id: string
    title: string
    userId?: string
    user: {
      id?: string
      firstName: string
      lastName: string
      avatar?: string
    }
  }
}

interface Message {
  id: string
  senderId: string
  receiverId: string
  content: string
  type: 'text' | 'file' | 'image' | 'system' | 'course_material'
  status: 'sent' | 'delivered' | 'read'
  createdAt: Date
  sender?: {
    id: string
    firstName: string
    lastName: string
    avatar?: string
  }
}

// 倒计时组件 - 增强版
const CountdownTimer = ({ targetDate }: { targetDate: Date }) => {
  const [timeLeft, setTimeLeft] = useState('')

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = targetDate.getTime() - new Date().getTime()
      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24))
        const hours = Math.floor((difference / (1000 * 60 * 60)) % 24)
        const minutes = Math.floor((difference / 1000 / 60) % 60)
        if (days > 0) return `${days}天 ${hours}小时后`
        return `${hours}小时 ${minutes}分钟后`
      }
      return '即将开始'
    }

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft())
    }, 60000)

    setTimeLeft(calculateTimeLeft())

    return () => clearInterval(timer)
  }, [targetDate])

  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/60">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
      </span>
      <span className="text-xs font-semibold text-amber-700">{timeLeft}</span>
    </div>
  )
}

// 进度环组件 - 优化版
const ProgressRing = ({ progress, size = 'md' }: { progress: number; size?: 'sm' | 'md' }) => {
  const dimensions = size === 'sm' ? { radius: 16, stroke: 2.5 } : { radius: 22, stroke: 3 }
  const { radius, stroke } = dimensions
  const normalizedRadius = radius - stroke * 2
  const circumference = normalizedRadius * 2 * Math.PI
  const strokeDashoffset = circumference - (progress / 100) * circumference

  const getColor = () => {
    if (progress === 100) return { ring: '#10B981', bg: '#D1FAE5' }
    if (progress >= 50) return { ring: '#3B82F6', bg: '#DBEAFE' }
    return { ring: '#F59E0B', bg: '#FEF3C7' }
  }

  const colors = getColor()

  return (
    <div className="relative flex items-center justify-center">
      <svg height={radius * 2} width={radius * 2} className="transform -rotate-90">
        <circle
          stroke={colors.bg}
          strokeWidth={stroke}
          fill="transparent"
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        <circle
          stroke={colors.ring}
          strokeWidth={stroke}
          strokeDasharray={circumference + ' ' + circumference}
          style={{ strokeDashoffset, transition: 'stroke-dashoffset 0.5s ease-out' }}
          strokeLinecap="round"
          fill="transparent"
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
      </svg>
      <span className="absolute text-[9px] font-bold" style={{ color: colors.ring }}>
        {progress}%
      </span>
    </div>
  )
}

// 状态徽章组件
const StatusBadge = ({ status }: { status: Booking['status'] }) => {
  const config = {
    pending: {
      icon: ClockSolid,
      label: '待确认',
      className: 'bg-amber-50 text-amber-700 border-amber-200'
    },
    confirmed: {
      icon: CheckCircleSolid,
      label: '已确认',
      className: 'bg-emerald-50 text-emerald-700 border-emerald-200'
    },
    completed: {
      icon: CheckBadgeIcon,
      label: '已完成',
      className: 'bg-blue-50 text-blue-700 border-blue-200'
    },
    cancelled: {
      icon: XCircleSolid,
      label: '已取消',
      className: 'bg-slate-50 text-slate-600 border-slate-200'
    }
  }

  const { icon: Icon, label, className } = config[status]

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${className}`}>
      <Icon className="w-3.5 h-3.5" />
      {label}
    </span>
  )
}

// 课程卡片组件 - 全新设计（支持时区转换）
const BookingCard = ({
  booking,
  onCancel,
  onShowMaterial,
  onContactTeacher
}: {
  booking: Booking
  onCancel: (booking: Booking) => void
  onShowMaterial: (booking: Booking) => void
  onContactTeacher: (booking: Booking) => void
}) => {
  // 使用 DateHelper 进行时区转换
  const dateStr = typeof booking.date === 'string' 
    ? booking.date 
    : DateHelper.formatDateForInput(new Date(booking.date))
  
  const { localDateObj, localStartTime, localEndTime, timezoneNote } = DateHelper.formatBookingTime(
    dateStr,
    booking.startTime,
    booking.endTime
  )
  
  const isUpcoming = booking.status === 'confirmed' && DateHelper.isUpcoming(dateStr, booking.startTime)
  const canCancel = booking.status === 'pending' || booking.status === 'confirmed'
  const weekday = DateHelper.getWeekdayName(localDateObj)

  return (
    <div className="group relative bg-white rounded-2xl border border-slate-100 overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-slate-200/50 hover:border-slate-200">
      {/* 顶部渐变装饰条 */}
      <div
        className={`h-1 w-full ${
          booking.status === 'confirmed'
            ? 'bg-gradient-to-r from-emerald-400 to-teal-500'
            : booking.status === 'pending'
            ? 'bg-gradient-to-r from-amber-400 to-orange-500'
            : booking.status === 'completed'
            ? 'bg-gradient-to-r from-blue-400 to-indigo-500'
            : 'bg-gradient-to-r from-slate-300 to-slate-400'
        }`}
      />

      <div className="p-5 sm:p-6">
        <div className="flex flex-col lg:flex-row gap-5">
          {/* 左侧：日期时间模块 */}
          <div className="flex-shrink-0 lg:w-44">
            <div className="flex lg:flex-col items-center lg:items-start gap-3 lg:gap-1">
              {/* 日期显示（已转换为本地时区） */}
              <div className="flex items-baseline gap-2">
                <span className="text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
                  {localDateObj.getDate()}
                </span>
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-slate-600">
                    {localDateObj.getMonth() + 1}月
                  </span>
                  <span className="text-xs text-slate-400">{weekday}</span>
                </div>
              </div>

              {/* 时间显示（已转换为本地时区） */}
              <div className="flex items-center gap-2 mt-1 lg:mt-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600">
                  <ClockIcon className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-sm font-bold text-slate-800">
                    {localStartTime}
                    {localEndTime && ` - ${localEndTime}`}
                  </div>
                  <div className="text-xs text-slate-400 flex items-center gap-1">
                    {booking.duration} 课时
                    {timezoneNote && (
                      <span className="text-indigo-500" title="已转换为您的本地时间">
                        <GlobeAltIcon className="w-3 h-3 inline" />
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* 倒计时/状态 */}
              <div className="mt-3 hidden lg:block">
                {isUpcoming ? (
                  <CountdownTimer targetDate={localDateObj} />
                ) : (
                  <StatusBadge status={booking.status} />
                )}
              </div>
            </div>
          </div>

          {/* 分隔线 */}
          <div className="hidden lg:block w-px bg-gradient-to-b from-transparent via-slate-200 to-transparent" />

          {/* 中间：课程信息 */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h3 className="text-lg font-bold text-slate-900 group-hover:text-indigo-600 transition-colors line-clamp-1">
                  {booking.course.title}
                </h3>
                <p className="text-sm text-slate-500 mt-0.5">{booking.course.subject}</p>
              </div>

              {/* 移动端状态显示 */}
              <div className="lg:hidden flex-shrink-0">
                {isUpcoming ? (
                  <CountdownTimer targetDate={localDateObj} />
                ) : (
                  <StatusBadge status={booking.status} />
                )}
              </div>

              {/* 进度环 */}
              <div className="hidden sm:block">
                {booking.status === 'confirmed' && <ProgressRing progress={0} />}
                {booking.status === 'completed' && <ProgressRing progress={100} />}
              </div>
            </div>

            {/* 教师信息 */}
            <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-slate-50/80 border border-slate-100">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white font-bold text-sm shadow-sm">
                {booking.teacher.user.firstName?.[0]}
                {booking.teacher.user.lastName?.[0]}
              </div>
              <div>
                <div className="text-sm font-semibold text-slate-800">
                  {booking.teacher.user.firstName} {booking.teacher.user.lastName}
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-1">
                  <StarIcon className="w-3 h-3 text-amber-400" />
                  专业导师
                </div>
              </div>
            </div>

            {/* 操作按钮区 */}
            <div className="flex flex-wrap items-center gap-2">
              {booking.status === 'confirmed' && (
                <button className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-sm font-semibold shadow-md shadow-indigo-200/50 hover:shadow-lg hover:shadow-indigo-300/50 transition-all hover:-translate-y-0.5">
                  <VideoCameraIcon className="w-4 h-4" />
                  进入教室
                </button>
              )}

              <button 
                onClick={() => onShowMaterial(booking)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 hover:border-slate-300 transition-all"
              >
                <DocumentTextIcon className="w-4 h-4" />
                课程资料
              </button>

              <button 
                onClick={() => onContactTeacher(booking)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-emerald-200 text-emerald-600 text-sm font-medium hover:bg-emerald-50 hover:border-emerald-300 transition-all"
              >
                <ChatBubbleLeftRightIcon className="w-4 h-4" />
                联系老师
              </button>

              {canCancel && (
                <button
                  onClick={() => onCancel(booking)}
                  className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-rose-600 text-sm font-medium hover:bg-rose-50 transition-all"
                >
                  取消预约
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// 空状态组件
const EmptyBookings = ({ filter }: { filter: string }) => {
  const messages: Record<string, { title: string; desc: string }> = {
    all: { title: '还没有任何课程预约', desc: '去发现精彩课程，开启学习之旅吧！' },
    pending: { title: '没有待确认的预约', desc: '所有预约都已处理完毕。' },
    confirmed: { title: '没有已确认的课程', desc: '预约课程后，确认信息将在此显示。' },
    completed: { title: '还没有完成的课程', desc: '完成课程后会在这里记录您的学习成果。' },
    cancelled: { title: '没有被取消的预约', desc: '这是好消息！' }
  }

  const { title, desc } = messages[filter] || messages.all

  return (
    <div className="text-center py-16 px-6">
      <div className="relative inline-flex mb-6">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-200 to-purple-200 rounded-full blur-xl opacity-60 animate-pulse" />
        <div className="relative w-20 h-20 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-full flex items-center justify-center border border-indigo-100">
          <CalendarIcon className="w-10 h-10 text-indigo-400" />
        </div>
      </div>
      <h3 className="text-xl font-bold text-slate-900 mb-2">{title}</h3>
      <p className="text-slate-500 mb-8 max-w-sm mx-auto">{desc}</p>
      <Link
        to="/courses"
        className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold shadow-lg shadow-indigo-200/50 hover:shadow-xl hover:shadow-indigo-300/50 transition-all hover:-translate-y-0.5"
      >
        <BookOpenIcon className="w-5 h-5" />
        探索课程
        <ArrowRightIcon className="w-4 h-4" />
      </Link>
    </div>
  )
}

// 课程资料弹窗组件
const CourseMaterialModal = ({
  booking,
  onClose
}: {
  booking: Booking
  onClose: () => void
}) => {
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200">
        {/* 头部 */}
        <div className="relative bg-gradient-to-r from-indigo-500 to-purple-600 p-6 text-white">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <DocumentTextIcon className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-bold">{booking.course.title}</h3>
              <p className="text-white/80 text-sm">{booking.course.subject}</p>
            </div>
          </div>
        </div>

        {/* 内容 */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* 课程描述 */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-slate-500 mb-2 flex items-center gap-2">
              <BookOpenIcon className="w-4 h-4" />
              课程介绍
            </h4>
            <p className="text-slate-700 leading-relaxed">
              {booking.course.description || '暂无课程描述，请联系老师了解更多信息。'}
            </p>
          </div>

          {/* 学习目标 */}
          {booking.course.learningObjectives && booking.course.learningObjectives.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-slate-500 mb-3 flex items-center gap-2">
                <AcademicCapIcon className="w-4 h-4" />
                学习目标
              </h4>
              <div className="space-y-2">
                {booking.course.learningObjectives.map((obj, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckIcon className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{obj}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 先修要求 */}
          {booking.course.requirements?.prerequisites && booking.course.requirements.prerequisites.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-slate-500 mb-3 flex items-center gap-2">
                <DocumentArrowDownIcon className="w-4 h-4" />
                先修要求
              </h4>
              <ul className="space-y-1 text-slate-700">
                {booking.course.requirements.prerequisites.map((req, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                    {req}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 上课信息（时间已转换为本地时区） */}
          <div className="bg-slate-50 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-slate-500 mb-3 flex items-center gap-2">
              上课信息
              {DateHelper.getLocalTimezone() !== 'Asia/Shanghai' && (
                <span className="text-xs text-indigo-500 flex items-center gap-1">
                  <GlobeAltIcon className="w-3 h-3" />
                  {DateHelper.getTimezoneDisplay()}
                </span>
              )}
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-500">上课日期</span>
                <p className="font-semibold text-slate-800">
                  {(() => {
                    const dateStr = typeof booking.date === 'string' 
                      ? booking.date 
                      : DateHelper.formatDateForInput(new Date(booking.date))
                    const { localDateObj } = DateHelper.formatBookingTime(dateStr, booking.startTime)
                    return localDateObj.toLocaleDateString('zh-CN', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })
                  })()}
                </p>
              </div>
              <div>
                <span className="text-slate-500">上课时间</span>
                <p className="font-semibold text-slate-800">
                  {(() => {
                    const dateStr = typeof booking.date === 'string' 
                      ? booking.date 
                      : DateHelper.formatDateForInput(new Date(booking.date))
                    const { localStartTime, localEndTime } = DateHelper.formatBookingTime(
                      dateStr, 
                      booking.startTime, 
                      booking.endTime
                    )
                    return `${localStartTime} - ${localEndTime || booking.endTime}`
                  })()}
                </p>
              </div>
              <div>
                <span className="text-slate-500">课程时长</span>
                <p className="font-semibold text-slate-800">{booking.duration} 课时</p>
              </div>
              <div>
                <span className="text-slate-500">授课老师</span>
                <p className="font-semibold text-slate-800">
                  {booking.teacher.user.firstName} {booking.teacher.user.lastName}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 底部按钮 */}
        <div className="p-6 pt-0">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-slate-100 text-slate-700 rounded-xl font-semibold hover:bg-slate-200 transition"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  )
}

// 联系老师/发消息弹窗组件
const MessageModal = ({
  booking,
  onClose
}: {
  booking: Booking
  onClose: () => void
}) => {
  const [messages, setMessages] = useState<Message[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const teacherUserId = booking.teacher.user?.id || booking.teacher.userId
  
  // 从多个可能的地方获取当前用户ID
  const getCurrentUserId = () => {
    let userId = localStorage.getItem('userId')
    if (userId) return userId
    
    const token = localStorage.getItem('token')
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]))
        if (payload.id) return payload.id
        if (payload.userId) return payload.userId
        if (payload.sub) return payload.sub
      } catch (e) { /* ignore */ }
    }
    
    const userStr = localStorage.getItem('user')
    if (userStr) {
      try {
        const user = JSON.parse(userStr)
        if (user.id) return user.id
      } catch (e) { /* ignore */ }
    }
    
    return null
  }
  
  const currentUserId = getCurrentUserId()

  useEffect(() => {
    loadMessages()
  }, [booking.id])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const loadMessages = async () => {
    try {
      const token = localStorage.getItem('token')
      
      // 使用对话 API 获取与老师的所有消息（而不仅仅是特定预约的消息）
      // 这样可以看到老师发送的所有消息，无论是否带有 bookingId
      if (teacherUserId) {
        const response = await fetch(`/api/messages/conversation/${teacherUserId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        
        if (response.ok) {
          const data = await response.json()
          setMessages(data.messages || [])
        }
      } else {
        // 如果没有老师用户ID，回退到按预约获取（兼容旧逻辑）
        const response = await fetch(`/api/messages/booking/${booking.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        
        if (response.ok) {
          const data = await response.json()
          setMessages(data.messages || [])
        }
      }
    } catch (error) {
      console.error('加载消息失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const sendMessage = async () => {
    if (!newMessage.trim() || sending || !teacherUserId) return

    setSending(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          receiverId: teacherUserId,
          content: newMessage.trim(),
          type: 'text',
          bookingId: booking.id,
          metadata: {
            courseId: booking.course.id,
            courseName: booking.course.title
          }
        })
      })

      if (response.ok) {
        const data = await response.json()
        setMessages(prev => [...prev, data.data])
        setNewMessage('')
        if (inputRef.current) inputRef.current.style.height = '48px'
        inputRef.current?.focus()
      } else {
        const errorData = await response.json()
        alert(errorData.message || '发送失败')
      }
    } catch (error) {
      console.error('发送消息失败:', error)
      alert('发送失败，请稍后重试')
    } finally {
      setSending(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full h-[85vh] max-h-[750px] overflow-hidden flex flex-col">
        {/* 头部 */}
        <div className="relative bg-gradient-to-r from-emerald-500 to-teal-600 px-5 py-4 text-white flex-shrink-0">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all hover:rotate-90"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-xl font-bold shadow-lg">
              {booking.teacher.user.firstName?.[0]}{booking.teacher.user.lastName?.[0]}
            </div>
            <div>
              <h3 className="text-xl font-bold">
                {booking.teacher.user.firstName} {booking.teacher.user.lastName}
              </h3>
              <p className="text-white/80 text-sm">{booking.teacher.title || '专业导师'}</p>
            </div>
          </div>
          <div className="mt-3 px-3 py-1.5 bg-white/20 backdrop-blur rounded-lg text-sm inline-flex items-center gap-2">
            <BookOpenIcon className="w-4 h-4" />
            {booking.course.title}
          </div>
        </div>

        {/* 消息列表 */}
        <div className="flex-1 overflow-y-auto px-4 py-4 bg-gradient-to-b from-slate-50/80 to-white">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-10 h-10 border-3 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-slate-400">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center mb-4">
                <ChatBubbleLeftRightIcon className="w-10 h-10 text-emerald-300" />
              </div>
              <p className="text-base font-medium text-slate-600">开始对话</p>
              <p className="text-sm mt-1 text-slate-400">发送消息与老师交流</p>
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((msg, index) => {
                const isMe = String(msg.senderId) === String(currentUserId)
                const nextMsg = messages[index + 1]
                const showAvatar = !isMe && (!nextMsg || String(nextMsg.senderId) === String(currentUserId))
                const teacherName = `${booking.teacher.user.firstName || ''} ${booking.teacher.user.lastName || ''}`.trim()
                
                return (
                  <div
                    key={msg.id}
                    className={`flex items-end gap-3 ${isMe ? 'flex-row-reverse pl-12' : 'flex-row pr-12'}`}
                  >
                    {/* 对方头像（老师） */}
                    {!isMe && (
                      <div className="w-9 h-9 flex-shrink-0">
                        {showAvatar ? (
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold shadow-md">
                            {booking.teacher.user.firstName?.[0]?.toUpperCase() || 'T'}
                          </div>
                        ) : (
                          <div className="w-9 h-9" />
                        )}
                      </div>
                    )}
                    
                    {/* 消息气泡 */}
                    <div className={`flex flex-col max-w-[75%] ${isMe ? 'items-end' : 'items-start'}`}>
                      {/* 发送者标签 */}
                      {showAvatar && !isMe && (
                        <span className="text-xs text-slate-500 mb-1 ml-1 font-medium">{teacherName || '老师'}</span>
                      )}
                      
                      <div
                        className={`relative px-4 py-3 rounded-2xl shadow-md ${
                          isMe
                            ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-br-sm'
                            : 'bg-white text-slate-800 border border-slate-200/60 rounded-bl-sm'
                        }`}
                      >
                        <p className="text-[15px] leading-relaxed whitespace-pre-wrap break-words">{msg.content}</p>
                      </div>
                      
                      {/* 时间和状态 */}
                      <div className={`flex items-center gap-2 mt-1.5 px-1 ${isMe ? 'flex-row-reverse' : ''}`}>
                        <span className="text-[11px] text-slate-400">
                          {new Date(msg.createdAt).toLocaleTimeString('zh-CN', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                        {isMe && msg.status === 'read' && (
                          <span className="text-emerald-500 text-[11px]">已读</span>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* 输入框 */}
        <div className="p-4 bg-white border-t border-slate-100 flex-shrink-0">
          <div className="flex items-end gap-3">
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={newMessage}
                onChange={(e) => {
                  setNewMessage(e.target.value)
                  e.target.style.height = 'auto'
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'
                }}
                onKeyDown={handleKeyDown}
                placeholder="输入消息..."
                className="w-full px-4 py-3 bg-slate-50 border-0 rounded-2xl text-[15px] resize-none focus:ring-2 focus:ring-emerald-500/20 focus:bg-white transition-all placeholder:text-slate-400"
                rows={1}
                style={{ minHeight: '48px', maxHeight: '120px' }}
              />
            </div>
            <button
              onClick={sendMessage}
              disabled={!newMessage.trim() || sending}
              className={`w-12 h-12 rounded-full flex items-center justify-center transition-all transform ${
                newMessage.trim() && !sending
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-200/50 hover:shadow-xl hover:scale-105 active:scale-95'
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'
              }`}
            >
              {sending ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <PaperAirplaneIcon className="w-5 h-5" />
              )}
            </button>
          </div>
          <p className="text-[11px] text-slate-400 mt-2 text-center">按 Enter 发送，Shift + Enter 换行</p>
        </div>
      </div>
    </div>
  )
}

// 快捷统计卡片
const QuickStatCard = ({
  icon: Icon,
  label,
  value,
  color
}: {
  icon: React.ElementType
  label: string
  value: number | string
  color: 'indigo' | 'emerald' | 'amber' | 'blue'
}) => {
  const colorClasses = {
    indigo: 'from-indigo-500 to-purple-600 shadow-indigo-200',
    emerald: 'from-emerald-500 to-teal-600 shadow-emerald-200',
    amber: 'from-amber-500 to-orange-600 shadow-amber-200',
    blue: 'from-blue-500 to-cyan-600 shadow-blue-200'
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3">
        <div
          className={`w-11 h-11 rounded-xl bg-gradient-to-br ${colorClasses[color]} shadow-lg flex items-center justify-center`}
        >
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <div className="text-2xl font-bold text-slate-900">{value}</div>
          <div className="text-xs text-slate-500">{label}</div>
        </div>
      </div>
    </div>
  )
}

const StudentBookings = () => {
  const [bookings, setBookings] = useState<Booking[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'pending' | 'confirmed' | 'completed' | 'cancelled'>('all')
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  
  // 新增弹窗状态
  const [showMaterialModal, setShowMaterialModal] = useState(false)
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [materialBooking, setMaterialBooking] = useState<Booking | null>(null)
  const [messageBooking, setMessageBooking] = useState<Booking | null>(null)

  // 显示课程资料
  const handleShowMaterial = (booking: Booking) => {
    setMaterialBooking(booking)
    setShowMaterialModal(true)
  }

  // 联系老师
  const handleContactTeacher = (booking: Booking) => {
    setMessageBooking(booking)
    setShowMessageModal(true)
  }

  useEffect(() => {
    fetchBookings()
  }, [])

  const fetchBookings = async () => {
    try {
      setError(null)
      const token = localStorage.getItem('token')
      if (!token) {
        setError('请先登录后查看预约')
        setLoading(false)
        return
      }
      const response = await fetch('/api/bookings/student', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      if (response.ok) {
        const data = await response.json()
        // 确保数据是数组
        setBookings(Array.isArray(data) ? data : [])
      } else if (response.status === 401) {
        setError('登录已过期，请重新登录')
      } else {
        const errorData = await response.json().catch(() => ({}))
        setError(errorData.message || '获取预约列表失败')
      }
    } catch (error) {
      console.error('获取预约列表失败:', error)
      setError('网络错误，请检查网络连接后重试')
    } finally {
      setLoading(false)
    }
  }

  const canCancelBooking = (booking: Booking) => {
    return booking.status === 'pending' || booking.status === 'confirmed'
  }

  const cancelBooking = async (bookingId: string) => {
    try {
      setCancelling(true)
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/bookings/${bookingId}/cancel`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: '学生主动取消' })
      })
      if (response.ok) {
        fetchBookings()
        setShowCancelModal(false)
        setSelectedBooking(null)
      } else {
        const errorData = await response.json().catch(() => ({}))
        alert(errorData.message || '取消预约失败')
      }
    } catch (error) {
      console.error('取消预约失败:', error)
      alert('取消预约失败，请稍后重试')
    } finally {
      setCancelling(false)
    }
  }

  const filteredBookings = bookings.filter((booking) => {
    if (filter === 'all') return true
    if (filter === 'cancelled') return booking.status === 'cancelled'
    return booking.status === filter
  })

  const stats = useMemo(() => {
    const upcoming = bookings.filter(
      (b) => b.status === 'confirmed' && new Date(b.date).getTime() >= new Date().setHours(0, 0, 0, 0)
    ).length
    const pending = bookings.filter((b) => b.status === 'pending').length
    const completed = bookings.filter((b) => b.status === 'completed').length
    return { total: bookings.length, upcoming, pending, completed }
  }, [bookings])

  const metrics = useMemo(
    () => [
      { label: '预约总数', value: stats.total || '—', helper: '全部课程记录' },
      { label: '即将上课', value: stats.upcoming, helper: stats.upcoming ? '待上课' : '暂无' },
      { label: '待确认', value: stats.pending, helper: '等待老师确认' },
      { label: '已完成', value: stats.completed, helper: '学习成果' }
    ],
    [stats]
  )

  const filterTabs = [
    { key: 'all', label: '全部', count: bookings.length },
    { key: 'pending', label: '待确认', count: stats.pending },
    { key: 'confirmed', label: '已确认', count: stats.upcoming },
    { key: 'completed', label: '已完成', count: stats.completed },
    { key: 'cancelled', label: '已取消', count: bookings.filter((b) => b.status === 'cancelled').length }
  ]

  if (loading) {
    return (
      <StudentPageLayout
        title="我的学习预约"
        description="管理课程预约，追踪学习进度"
        metrics={metrics}
      >
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-4 border-indigo-100 border-t-indigo-500 animate-spin" />
            <SparklesIcon className="w-6 h-6 text-indigo-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
          </div>
          <p className="text-slate-500 text-sm">正在加载预约信息...</p>
        </div>
      </StudentPageLayout>
    )
  }

  if (error) {
    return (
      <StudentPageLayout
        title="我的学习预约"
        description="管理课程预约，追踪学习进度"
        metrics={[]}
      >
        <div className="flex flex-col items-center justify-center py-16 px-6">
          <div className="relative inline-flex mb-6">
            <div className="absolute inset-0 bg-gradient-to-br from-rose-200 to-orange-200 rounded-full blur-xl opacity-60" />
            <div className="relative w-20 h-20 bg-gradient-to-br from-rose-50 to-orange-50 rounded-full flex items-center justify-center border border-rose-100">
              <XCircleSolid className="w-10 h-10 text-rose-400" />
            </div>
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">加载失败</h3>
          <p className="text-slate-500 mb-6 max-w-sm mx-auto text-center">{error}</p>
          <button
            onClick={() => {
              setLoading(true)
              fetchBookings()
            }}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold shadow-lg shadow-indigo-200/50 hover:shadow-xl transition-all"
          >
            <ArrowRightIcon className="w-4 h-4 rotate-180" />
            重新加载
          </button>
        </div>
      </StudentPageLayout>
    )
  }

  return (
    <StudentPageLayout
      title="我的学习预约"
      description="查看上课安排、进入教室或管理预约，AI 会自动同步课表与提醒。"
      metrics={metrics}
      actions={
        <Link
          to="/courses"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/15 backdrop-blur-sm text-white text-sm font-medium hover:bg-white/25 transition border border-white/20"
        >
          <BookOpenIcon className="w-4 h-4" />
          探索更多课程
        </Link>
      }
    >
      {/* 快捷统计 - 移动端横向滚动 */}
      <div className="overflow-x-auto -mx-4 px-4 pb-2 mb-6 sm:mb-8 sm:overflow-visible sm:mx-0 sm:px-0 sm:pb-0">
        <div className="flex gap-2 sm:gap-4 min-w-max sm:min-w-0 sm:grid sm:grid-cols-2 lg:grid-cols-4">
          <div className="min-w-[140px] sm:min-w-0">
            <QuickStatCard icon={CalendarIcon} label="全部预约" value={stats.total} color="indigo" />
          </div>
          <div className="min-w-[140px] sm:min-w-0">
            <QuickStatCard icon={ClockIcon} label="即将上课" value={stats.upcoming} color="emerald" />
          </div>
          <div className="min-w-[140px] sm:min-w-0">
            <QuickStatCard icon={ClockSolid} label="待确认" value={stats.pending} color="amber" />
          </div>
          <div className="min-w-[140px] sm:min-w-0">
            <QuickStatCard icon={CheckCircleSolid} label="已完成" value={stats.completed} color="blue" />
          </div>
        </div>
      </div>

      {/* 筛选标签 - 移动端优化 */}
      <div className="overflow-x-auto pb-2 mb-4 sm:mb-6 -mx-4 px-4 sm:mx-0 sm:px-0">
        <div className="flex gap-2 min-w-max sm:min-w-0 sm:flex-wrap">
          {filterTabs.map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setFilter(key as typeof filter)}
              className={`whitespace-nowrap px-3 sm:px-5 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all flex items-center gap-1.5 sm:gap-2 ${
                filter === key
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-200/50'
                  : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200 hover:border-slate-300'
              }`}
            >
              {label}
              <span
                className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded-full ${
                  filter === key ? 'bg-white/20' : 'bg-slate-100 text-slate-500'
                }`}
              >
                {count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* 预约列表 */}
      <StudentSection
        title="课程预约列表"
        description={`共 ${filteredBookings.length} 条${filter === 'all' ? '' : '符合筛选条件的'}记录`}
        icon={CalendarIcon}
      >
        {filteredBookings.length === 0 ? (
          <EmptyBookings filter={filter} />
        ) : (
          <div className="space-y-4">
            {filteredBookings.map((booking) => (
              <BookingCard
                key={booking.id}
                booking={booking}
                onCancel={(b) => {
                  setSelectedBooking(b)
                  setShowCancelModal(true)
                }}
                onShowMaterial={handleShowMaterial}
                onContactTeacher={handleContactTeacher}
              />
            ))}
          </div>
        )}
      </StudentSection>

      {/* 取消确认弹窗 */}
      {showCancelModal && selectedBooking && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="p-6">
              <div className="w-14 h-14 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-4">
                <XCircleSolid className="w-7 h-7 text-rose-500" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 text-center mb-2">确认取消预约？</h3>
              <p className="text-slate-500 text-center text-sm mb-6">
                取消后，该时段将被释放。如果是付费课程，退款将按照平台规则处理。
              </p>

              <div className="bg-slate-50 rounded-xl p-4 mb-6">
                <div className="text-sm font-semibold text-slate-800 mb-1">{selectedBooking.course.title}</div>
                <div className="text-xs text-slate-500">
                  {new Date(selectedBooking.date).toLocaleDateString('zh-CN')} · {selectedBooking.startTime}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCancelModal(false)}
                  disabled={cancelling}
                  className="flex-1 px-5 py-3 border border-slate-200 rounded-xl text-slate-700 font-semibold hover:bg-slate-50 transition disabled:opacity-50"
                >
                  我再想想
                </button>
                <button
                  onClick={() => cancelBooking(selectedBooking.id)}
                  disabled={cancelling}
                  className="flex-1 px-5 py-3 bg-gradient-to-r from-rose-500 to-pink-600 text-white rounded-xl font-semibold shadow-lg shadow-rose-200/50 hover:shadow-xl transition disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {cancelling ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      取消中...
                    </>
                  ) : (
                    '确认取消'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* 课程资料弹窗 */}
      {showMaterialModal && materialBooking && (
        <CourseMaterialModal
          booking={materialBooking}
          onClose={() => {
            setShowMaterialModal(false)
            setMaterialBooking(null)
          }}
        />
      )}

      {/* 联系老师弹窗 */}
      {showMessageModal && messageBooking && (
        <MessageModal
          booking={messageBooking}
          onClose={() => {
            setShowMessageModal(false)
            setMessageBooking(null)
          }}
        />
      )}
    </StudentPageLayout>
  )
}

export default StudentBookings
